import { getXtreamBase } from '../config/env';
import type {
  XtreamAuthResponse,
  XtreamCategory,
  XtreamEpgApiResponse,
  XtreamEpgListing,
  XtreamLiveStream,
  XtreamSeries,
  XtreamSeriesInfo,
  XtreamUserInfo,
  XtreamVodStream,
} from '../types/xtream';
import { epgEndMs, epgStartMs } from '../utils/epg';

export class XtreamApiError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'XtreamApiError';
    this.statusCode = statusCode;
  }
}

export interface XtreamCredentials {
  username: string;
  password: string;
}

function playerApiUrl(creds: XtreamCredentials, extra?: Record<string, string>): string {
  const base = getXtreamBase();
  const params = new URLSearchParams({
    username: creds.username,
    password: creds.password,
    ...extra,
  });
  return `${base}/player_api.php?${params.toString()}`;
}

async function xtreamFetch<T>(url: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    const hint = import.meta.env.DEV
      ? ' Uruchom `npm run dev` na porcie 5174 — żądania idą przez proxy /api.'
      : ' Na produkcji dodaj origin playera do IPTV_CORS_ORIGINS lub skonfiguruj reverse proxy /api na subdomenie playera.';
    throw new XtreamApiError(`Brak połączenia z serwerem Xtream.${hint}`);
  }
  if (!res.ok) {
    if (res.status === 429) {
      throw new XtreamApiError('Osiągnięto limit jednoczesnych połączeń (max_connections).', 429);
    }
    if (res.status === 500) {
      const ct = res.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) {
        throw new XtreamApiError(
          'Błąd proxy API na subdomenie playera (500). Odśwież stronę po deployu lub skonfiguruj ProxyPass /api/ w Apache.',
          500,
        );
      }
    }
    throw new XtreamApiError(`Błąd serwera (${res.status})`, res.status);
  }
  const text = await res.text();
  if (!text.trim()) {
    throw new XtreamApiError('Pusta odpowiedź serwera');
  }
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new XtreamApiError('Nieprawidłowa odpowiedź JSON z API');
  }
  return data as T;
}

export function isAuthOk(userInfo?: XtreamUserInfo): boolean {
  if (!userInfo) return false;
  if (userInfo.auth === 0) return false;
  const status = (userInfo.status || '').toLowerCase();
  if (status === 'banned' || status === 'disabled' || status === 'expired') return false;
  return userInfo.auth === 1 || status === 'active' || status === '';
}

export function authErrorMessage(userInfo?: XtreamUserInfo): string {
  if (!userInfo) return 'Brak danych użytkownika z API';
  if (userInfo.message?.trim()) return userInfo.message.trim();
  const status = userInfo.status || 'Unknown';
  return `Logowanie nieudane (status: ${status})`;
}

export async function xtreamLogin(creds: XtreamCredentials): Promise<XtreamAuthResponse> {
  const data = await xtreamFetch<XtreamAuthResponse>(playerApiUrl(creds));
  if (!isAuthOk(data.user_info)) {
    throw new XtreamApiError(authErrorMessage(data.user_info));
  }
  return data;
}

export async function xtreamAction<T>(
  creds: XtreamCredentials,
  action: string,
  extra?: Record<string, string>,
): Promise<T> {
  const url = playerApiUrl(creds, { action, ...extra });
  return xtreamFetch<T>(url);
}

export async function getLiveCategories(creds: XtreamCredentials): Promise<XtreamCategory[]> {
  const data = await xtreamAction<XtreamCategory[]>(creds, 'get_live_categories');
  return Array.isArray(data) ? data : [];
}

export async function getLiveStreams(creds: XtreamCredentials): Promise<XtreamLiveStream[]> {
  const data = await xtreamAction<XtreamLiveStream[]>(creds, 'get_live_streams');
  return Array.isArray(data) ? data : [];
}

export interface XtreamLiveCatalogResponse {
  categories: XtreamCategory[];
  streams: XtreamLiveStream[];
}

/** Jedno żądanie: kategorie + kanały live (szybsze niż dwa osobne get_live_*). */
export async function getLiveCatalog(creds: XtreamCredentials): Promise<XtreamLiveCatalogResponse> {
  const data = await xtreamAction<XtreamLiveCatalogResponse>(creds, 'get_live_catalog');
  return {
    categories: Array.isArray(data?.categories) ? data.categories : [],
    streams: Array.isArray(data?.streams) ? data.streams : [],
  };
}

export async function getShortEpg(
  creds: XtreamCredentials,
  streamId: number,
  limit = 4,
): Promise<XtreamEpgApiResponse> {
  return xtreamAction<XtreamEpgApiResponse>(creds, 'get_short_epg', {
    stream_id: String(streamId),
    limit: String(limit),
  });
}

export async function getSimpleDataTable(
  creds: XtreamCredentials,
  streamId: number,
): Promise<XtreamEpgApiResponse> {
  return xtreamAction<XtreamEpgApiResponse>(creds, 'get_simple_data_table', {
    stream_id: String(streamId),
  });
}

export async function getVodCategories(creds: XtreamCredentials): Promise<XtreamCategory[]> {
  const data = await xtreamAction<XtreamCategory[]>(creds, 'get_vod_categories');
  return Array.isArray(data) ? data : [];
}

export async function getVodStreams(creds: XtreamCredentials): Promise<XtreamVodStream[]> {
  const data = await xtreamAction<XtreamVodStream[]>(creds, 'get_vod_streams');
  return Array.isArray(data) ? data : [];
}

export async function getSeriesCategories(creds: XtreamCredentials): Promise<XtreamCategory[]> {
  const data = await xtreamAction<XtreamCategory[]>(creds, 'get_series_categories');
  return Array.isArray(data) ? data : [];
}

export async function getSeries(creds: XtreamCredentials): Promise<XtreamSeries[]> {
  const data = await xtreamAction<XtreamSeries[]>(creds, 'get_series');
  return Array.isArray(data) ? data : [];
}

export async function getSeriesInfo(
  creds: XtreamCredentials,
  seriesId: number,
): Promise<XtreamSeriesInfo> {
  return xtreamAction(creds, 'get_series_info', { series_id: String(seriesId) });
}

export type StreamKind = 'live' | 'movie' | 'series';

function streamExtension(preferHls: boolean): string {
  return preferHls ? 'm3u8' : 'ts';
}

/** URL odtwarzania live / vod / odcinka serialu. */
export function buildStreamUrl(
  creds: XtreamCredentials,
  kind: StreamKind,
  streamId: number | string,
  containerExtension?: string,
): string {
  const base = getXtreamBase();
  const user = encodeURIComponent(creds.username);
  const pass = encodeURIComponent(creds.password);
  const id = encodeURIComponent(String(streamId));

  if (kind === 'movie') {
    const ext = containerExtension || 'mp4';
    return `${base}/movie/${user}/${pass}/${id}.${ext}`;
  }
  if (kind === 'series') {
    const ext = containerExtension || 'mp4';
    return `${base}/series/${user}/${pass}/${id}.${ext}`;
  }

  const ext = containerExtension || streamExtension(true);
  return `${base}/live/${user}/${pass}/${id}.${ext}`;
}

/** Catchup / timeshift — standardowy format Xtream (utc + lutc + opcjonalnie start/end). */
export function buildCatchupUrl(
  creds: XtreamCredentials,
  streamId: number,
  startUtc: number,
  durationMinutes: number,
  endUtc?: number,
): string {
  const base = getXtreamBase();
  const user = encodeURIComponent(creds.username);
  const pass = encodeURIComponent(creds.password);
  const id = encodeURIComponent(String(streamId));
  const now = Math.floor(Date.now() / 1000);
  const durationSec =
    endUtc !== undefined && endUtc > startUtc
      ? Math.max(60, endUtc - startUtc)
      : Math.max(60, durationMinutes * 60);

  const params = new URLSearchParams({
    utc: String(startUtc),
    lutc: String(now),
    duration: String(durationSec),
    start: String(startUtc),
  });
  if (endUtc !== undefined && endUtc > startUtc) {
    params.set('end', String(endUtc));
  }

  return `${base}/live/${user}/${pass}/${id}.m3u8?${params.toString()}`;
}

/** URL archiwum z wpisu EPG (start/end z programu). */
export function buildCatchupUrlFromListing(
  creds: XtreamCredentials,
  streamId: number,
  listing: XtreamEpgListing,
): string {
  const startMs = epgStartMs(listing);
  const endMs = epgEndMs(listing);
  const startUtc = Math.floor(startMs / 1000);
  const endUtc = endMs > startMs ? Math.floor(endMs / 1000) : undefined;
  const durationMin =
    startMs && endMs
      ? Math.max(1, Math.round((endMs - startMs) / 60000))
      : 60;
  return buildCatchupUrl(creds, streamId, startUtc, durationMin, endUtc);
}

export function isCatchupStreamUrl(url: string): boolean {
  return /[?&]utc=/i.test(url);
}

export function isLiveStreamUrl(url: string): boolean {
  return /\/live\//i.test(url) && !isCatchupStreamUrl(url);
}

export function channelHasArchive(ch: XtreamLiveStream): boolean {
  return (
    ch.tv_archive === 1 ||
    (typeof ch.tv_archive_duration === 'number' && ch.tv_archive_duration > 0)
  );
}
